// src/actions/ai_actions.js
import axios from "axios";
import {
  FETCH_AI_CARDS_LOADING, FETCH_AI_CARDS_SUCCESS, FETCH_AI_CARDS_FAIL,
  SEND_AI_MESSAGE_LOADING, SEND_AI_MESSAGE_SUCCESS, SEND_AI_MESSAGE_FAIL,
  FETCH_AI_SESSIONS_LOADING, FETCH_AI_SESSIONS_SUCCESS, FETCH_AI_SESSIONS_FAIL,
  FETCH_AI_SESSION_HISTORY_LOADING, FETCH_AI_SESSION_HISTORY_SUCCESS, FETCH_AI_SESSION_HISTORY_FAIL,
  ADD_USER_MESSAGE, AI_RECEIVE_MESSAGE, CLEAR_AI_HISTORY, CREATE_AI_SESSION_REQUEST,
  CREATE_AI_SESSION_SUCCESS, CREATE_AI_SESSION_FAILURE, FETCH_AI_ANALYTICS_HISTORY_LOADING,
  FETCH_AI_ANALYTICS_HISTORY_SUCCESS, FETCH_AI_ANALYTICS_HISTORY_FAIL
} from "./types";

// Base URL
const API_URL = process.env.REACT_APP_ADMIN_API_ADDRESS;

export const createAISessionOnServer = (userId, sessionId) => async dispatch => {
  dispatch({ type: CREATE_AI_SESSION_REQUEST, payload: sessionId });

  try {
    const token = localStorage.getItem('jwtToken');
    await axios.post(
      `${API_URL}/secureApi/session`,
      { user_id: userId, session_id: sessionId },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    dispatch({ type: CREATE_AI_SESSION_SUCCESS, payload: sessionId });

  } catch (err) {
    console.error('create session failed', err);
    dispatch({
      type: CREATE_AI_SESSION_FAILURE,
      payload: err.response?.data?.message || err.message
    });
  }
};

export const clearAIHistory = () => ({
  type: CLEAR_AI_HISTORY
});

// Action creator for the incoming AI message
export const receiveAIMessage = (payload) => ({
  type:    AI_RECEIVE_MESSAGE,
  payload
});

// simple synchronous action
export const addUserMessage = (text) => ({
  type: ADD_USER_MESSAGE,
  payload: { from: 'user', text }
});

// Load AI Action Cards
export const fetchAICards = () => async (dispatch) => {
  dispatch({ type: FETCH_AI_CARDS_LOADING });
  try {
    const token = localStorage.getItem("jwtToken");
    const { data } = await axios.get(`${API_URL}/secureApi/aicards`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    dispatch({ type: FETCH_AI_CARDS_SUCCESS, payload: data });
  } catch (err) {
    dispatch({ type: FETCH_AI_CARDS_FAIL, payload: err.response?.data?.message || err.message });
  }
};

// Send AI Chat Message
export const sendAIMessage = (message) => async (dispatch) => {
  console.group("[sendAIMessage]");
  console.debug("ACTION MESSAGE ▶", message);

  // 1) show typing indicator
  dispatch({ type: SEND_AI_MESSAGE_LOADING, payload: message });

  const token = localStorage.getItem("jwtToken");
  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    timeout: 50000,
  };

  try {
    // 2) fire off your chat POST
    const { data } = await axios.post(
      `${API_URL}/secureApi/chat?multi=on`,
      message,
      config
    );
    console.debug("SUCCESS RESPONSE ▶", data);

    // 3) clear the typing indicator
    dispatch({ type: SEND_AI_MESSAGE_SUCCESS });

    // 4) assemble a full payload
    const aiPayload = {
      sessionId: message.session_id,
      from: "ai",
      // preserve everything your API returned:
      ...data,
      // normalize a `text` field if you still rely on it:
      text: data.response || data.reply || data.message || "",
    };

    // 5) dispatch the full‐object payload
    dispatch({
      type: AI_RECEIVE_MESSAGE,
      payload: aiPayload,
    });

  } catch (error) {
    console.error("ERROR ▶", {
      message: error.message,
      request: error.request,
      response: error.response?.data,
      status: error.response?.status,
    });

    // normalize an error string
    let errorMsg = "An unexpected error occurred";
    if (error.response?.data?.message) {
      errorMsg = error.response.data.message;
    } else if (error.request) {
      errorMsg = "No response from server (network issue?)";
    } else {
      errorMsg = error.message;
    }

    // append an error bubble
    dispatch({
      type: SEND_AI_MESSAGE_FAIL,
      payload: { from: "ai", text: errorMsg },
      meta: { status: error.response?.status },
    });
  } finally {
    console.groupEnd();
  }
};

// Fetch AI Sessions
export const fetchAISessions = (userId) => async (dispatch) => {
  dispatch({ type: FETCH_AI_SESSIONS_LOADING });
  try {
    const token = localStorage.getItem("jwtToken");
    const { data } = await axios.get(
      `${API_URL}/secureApi/user/${encodeURIComponent(userId)}/sessions`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    // NEW: map each session → { id, title, createdAt }
    const sessions = (data.sessions || []).map(s => ({
      id:        s.session_id,
      title:     s.session_id,                              // or s.title if your API gives one
      createdAt: new Date(s.last_message_time).getTime()
    }));

    dispatch({
      type:    FETCH_AI_SESSIONS_SUCCESS,
      payload: sessions     // <- now a true Array of {id, title, createdAt}
    });

  } catch (err) {
    dispatch({
      type: FETCH_AI_SESSIONS_FAIL,
      payload: err.response?.data?.message || err.message
    });
  }
};

// Fetch AI Session History

export const fetchAISessionHistory = sessionId => async dispatch => {
  try {
    const token = localStorage.getItem('jwtToken');
    const { data } = await axios.get(
      `${API_URL}/secureApi/session/${sessionId}/history`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    // 1) Grab the raw history array (or an empty array)
    const fullHistory = data.history || [];

    // 2) Optional: attach a `from` field and filter out internal tool-returns
    const enriched = fullHistory
      .filter(item => !item.parts?.some(p => p.part_kind === 'tool-return'))
      .map(item => ({
        ...item, // preserve every field
        from: item.kind === 'request' ? 'user' : 'bot',
        text: item.parts?.[0]?.content ?? undefined
      }));

      // Turn each mixlist into a pseudo‐message
      const mixlistMessages = (data.barsys?.mixlists || []).map(mix =>
        ({
          from: "bot",
          mixlist: mix,
          text: ""      // you can render mixlist via `mixlist` prop in your component
        })
      );

    dispatch({
      type: FETCH_AI_SESSION_HISTORY_SUCCESS,
      payload: [...enriched, ...mixlistMessages]
    });
  } catch (err) {
    console.warn('History fetch failed, falling back to empty array', err);
    // treat every error as “no history yet”
    dispatch({
      type: FETCH_AI_SESSION_HISTORY_SUCCESS,
      payload: []
    });
  }
};

// Fetch AI Analytics Session Messages (new endpoint)
export const fetchAIAnalyticsHistory = (
  sessionId,
  limit = 20,
  offset = 0
) => async dispatch => {
  dispatch({ type: FETCH_AI_ANALYTICS_HISTORY_LOADING });

  try {
    const token = localStorage.getItem('jwtToken');

    const { data } = await axios.get(
      `${API_URL}/secureApi/session/${encodeURIComponent(sessionId)}/analytics-messages`,
      {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit, offset }
      }
    );

   // NEW: { session_id, user_id, messages: [ { user_message, bot_message, action_cards, ai_generated_recipe, barsys_recipe, barsys_mixlist, created_at, ... } ] }
   const turns = Array.isArray(data?.messages) ? data.messages : [];
   const mapped = [];
   for (const t of turns) {
     const ts = t.created_at;
     // 1) user bubble (only if present)
     if (t.user_message && t.user_message.trim() !== '') {
       mapped.push({
         from: 'user',
         text: t.user_message,
         created_at: ts
       });
     }
     // 2) bot bubble (only if present)
     if (t.bot_message && t.bot_message.trim() !== '') {
       mapped.push({
         from: 'bot',
         text: t.bot_message,
         created_at: ts,
         // pass-through rich payload so UI can render like before
         action_cards: Array.isArray(t.action_cards) ? t.action_cards : [],
         ai_generated_recipe: Array.isArray(t.ai_generated_recipe) ? t.ai_generated_recipe : null,
         barsys_recipe: Array.isArray(t.barsys_recipe) ? t.barsys_recipe : null,
         barsys: {
           // keep shape compatible with your renderer
           mixlists: Array.isArray(t.barsys_mixlist) ? t.barsys_mixlist : (t.barsys_mixlist ? [t.barsys_mixlist] : [])
         }
       });
     }
   }

    const total = Number.isFinite(data?.total_messages) ? data.total_messages : turns.length;
    const pageTurns = turns.length; // <-- count of raw turns this page

    dispatch({
      type: FETCH_AI_ANALYTICS_HISTORY_SUCCESS,
      payload: mapped,
      meta: { offset, total, pageTurns } // so reducer knows whether to prepend or replace
    });

    // Return mapped so component can check length for hasMore
    return mapped;
  } catch (err) {
    dispatch({
      type: FETCH_AI_ANALYTICS_HISTORY_FAIL,
      payload: err.response?.data?.message || err.message
    });
    return [];
  }
};




